"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.listDepositsResponseSchema = exports.listDepositsQuerySchema = exports.createDepositResponseSchema = exports.createDepositBodySchema = exports.listDepositsParamsSchema = exports.createDepositParamsSchema = exports.CreditTransactionEntitySchema = exports.UserCreditEntitySchema = exports.CreditTransactionTypeSchema = void 0;
const zod_1 = require("zod");
const common_schemas_1 = require("../base/common.schemas");
const api_schemas_1 = require("../base/api.schemas");
const timestamp_schemas_1 = require("../base/timestamp.schemas");
// ============================================
// INTERNAL SCHEMAS (Entity)
// ============================================
// Credit transaction types (from credit_transactions.transaction_type CHECK constraint)
const _CreditTransactionTypeSchema = zod_1.z.enum(['deposit', 'bonus', 'spend', 'refund', 'adjustment']);
// Credit transaction reference types (no CHECK constraint in DB, just varchar)
// Values: 'deposit', 'redemption', 'refund', 'adjustment'
// User credit entity schema matching database user_credits table
const _UserCreditBaseSchema = zod_1.z.object({
    credit_id: zod_1.z.number().int().positive(),
    user_id: common_schemas_1.uuidSchema,
    merchant_id: zod_1.z.number().int().positive(),
    balance: common_schemas_1.moneyAmountSchema,
    created_at: timestamp_schemas_1.timestampIsoSchema,
    updated_at: timestamp_schemas_1.timestampIsoSchema,
});
// Credit transaction entity schema matching database credit_transactions table
const _CreditTransactionBaseSchema = zod_1.z.object({
    transaction_id: zod_1.z.number().int().positive(),
    user_id: common_schemas_1.uuidSchema,
    merchant_id: zod_1.z.number().int().positive(),
    amount: common_schemas_1.signedMoneyAmountSchema, // can be positive or negative
    transaction_type: _CreditTransactionTypeSchema,
    balance_after: common_schemas_1.moneyAmountSchema,
    reference_id: zod_1.z.number().int().positive().nullable(),
    reference_type: zod_1.z.string().max(20).nullable(), // varchar(20) in DB, no CHECK
    created_at: timestamp_schemas_1.timestampIsoSchema,
});
// ============================================
// ENTITY SCHEMAS (Export Aliases)
// ============================================
exports.CreditTransactionTypeSchema = _CreditTransactionTypeSchema;
exports.UserCreditEntitySchema = _UserCreditBaseSchema;
exports.CreditTransactionEntitySchema = _CreditTransactionBaseSchema;
// ============================================
// MERCHANT CREDITS API SCHEMAS (Prepayment)
// ============================================
// Common merchant params schema
const _MerchantParamsSchema = zod_1.z.object({
    merchantId: zod_1.z.coerce.number().int().positive(),
});
// Params schemas for each endpoint
exports.createDepositParamsSchema = _MerchantParamsSchema;
exports.listDepositsParamsSchema = _MerchantParamsSchema;
// POST /merchants/:merchantId/credits/deposit - Body (FR66, FR197-203)
//
// PREPAYMENT FLOW (FR197-203):
// 1. Dedicated page for prepayment recording
// 2. Select member from list (FR198)
// 3. Enter prepayment amount (FR199)
// 4. Select reward tier if applicable (FR200)
// 5. Show preview of rewards (FR201)
// 6. Confirm -> adds credit + bonus + coupons (FR202-203)
// 7. Customer gets push notification
//
exports.createDepositBodySchema = zod_1.z.object({
    user_id: common_schemas_1.uuidSchema,
    amount: common_schemas_1.moneyAmountSchema.positive(), // Cash received (FR199)
    reward_tier_id: zod_1.z.number().int().positive().nullable(), // Optional tier (FR200)
});
// POST /merchants/:merchantId/credits/deposit - Response
exports.createDepositResponseSchema = (0, api_schemas_1.dataResponseSchema)(zod_1.z.object({
    deposit_id: zod_1.z.number().int(),
    amount_deposited: common_schemas_1.moneyAmountSchema,
    bonus_credit: common_schemas_1.moneyAmountSchema, // From tier (FR206)
    total_credit_added: common_schemas_1.moneyAmountSchema,
    new_balance: common_schemas_1.moneyAmountSchema,
    coupons_awarded: zod_1.z.number().int(), // Count of coupons from tier (FR73)
}));
// GET /merchants/:merchantId/credits/deposits - Query
exports.listDepositsQuerySchema = zod_1.z.object({
    userId: common_schemas_1.uuidSchema.optional(),
    startDate: timestamp_schemas_1.timestampIsoSchema.optional(),
    endDate: timestamp_schemas_1.timestampIsoSchema.optional(),
    limit: zod_1.z.coerce.number().int().min(1).max(100).default(50),
    cursor: zod_1.z.string().optional(),
});
// Internal schema for deposit item in list
const _DepositItemSchema = zod_1.z.object({
    deposit_id: zod_1.z.number().int(),
    user_id: zod_1.z.string(),
    user_display_name: zod_1.z.string().nullable(),
    amount: common_schemas_1.moneyAmountSchema,
    bonus_credit: common_schemas_1.moneyAmountSchema,
    reward_tier_name: zod_1.z.string().nullable(),
    timestamp: timestamp_schemas_1.timestampIsoSchema,
});
// GET /merchants/:merchantId/credits/deposits - Response
exports.listDepositsResponseSchema = (0, api_schemas_1.listResponseSchema)(_DepositItemSchema);
